import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  MY_LINKS_TAB_ID,
  SCOPE_PERSONAL,
  SCOPE_SHARED,
  coerceSharedShortcutsPayload,
  coerceShortcutsPayload,
  defaultSharedShortcutsPayload,
  defaultShortcutsPayload,
  mergeShortcutViews,
  moveShortcutOrder,
  moveTabOrder,
  newEntityId,
  replaceTabShortcuts,
  shortcutsForTab,
  utcNow,
} from '../../services/shortcuts/shortcutModel';
import {
  loadUserShortcuts,
  saveSharedShortcuts,
  saveUserShortcuts,
  shortcutConflictFromError,
} from '../../services/shortcuts/shortcutPersistenceService';
import { normalizeShortcutUrl } from '../../services/shortcuts/shortcutUrl';
import {
  launcherIconContextToken,
  syncLauncherIconContext,
} from '../../services/homeLauncher/launcherIconCache';

const SHARED_POLL_MS = 20000;

function emptyBundle(tenantId) {
  return {
    personal: defaultShortcutsPayload(tenantId),
    shared: defaultSharedShortcutsPayload(tenantId),
    canManageShared: false,
  };
}

export default function useUserShortcuts({ uid, tenantId, poll = true }) {
  const [bundle, setBundle] = useState(() => emptyBundle(tenantId));
  const [activeTabId, setActiveTabId] = useState(MY_LINKS_TAB_ID);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const bundleRef = useRef(bundle);
  const requestGen = useRef(0);
  const savingRef = useRef(false);
  const sharedDirtyRef = useRef(false);
  const [sharedConflict, setSharedConflict] = useState(null);

  useEffect(() => {
    bundleRef.current = bundle;
  }, [bundle]);

  const merged = useMemo(
    () => mergeShortcutViews(bundle.personal, bundle.shared),
    [bundle.personal, bundle.shared]
  );

  const clearLocal = useCallback((nextTenantId = null) => {
    const empty = emptyBundle(nextTenantId);
    bundleRef.current = empty;
    sharedDirtyRef.current = false;
    setSharedConflict(null);
    setBundle(empty);
    setActiveTabId(MY_LINKS_TAB_ID);
    setError(null);
    setSaveError(null);
    setSaving(false);
  }, []);

  const applyBundle = useCallback((next, { keepTab = true } = {}) => {
    bundleRef.current = next;
    setBundle(next);
    const ids = new Set(mergeShortcutViews(next.personal, next.shared).tabs.map(tab => tab.id));
    setActiveTabId(prev => {
      if (keepTab && ids.has(prev)) return prev;
      return next.personal.tabs[0]?.id || MY_LINKS_TAB_ID;
    });
  }, []);

  const load = useCallback(
    async (gen, { silent = false } = {}) => {
      if (!uid || !tenantId) return;
      if (!silent) setLoading(true);
      try {
        const next = await loadUserShortcuts(tenantId);
        if (requestGen.current !== gen) return;
        if (silent && (savingRef.current || sharedDirtyRef.current)) return;
        applyBundle(next);
        if (!silent) {
          sharedDirtyRef.current = false;
          setSharedConflict(null);
        }
        setError(null);
      } catch (err) {
        if (requestGen.current !== gen) return;
        const message = err?.message || 'Could not load shortcuts';
        const denied = /API Error \(40[139]\)|Not a member|Tenant context|not authenticated|support_grant|support_route|mixed_context|tenant_conflict/i.test(
          message
        );
        if (denied) {
          clearLocal(tenantId || null);
        }
        if (message && !denied) console.warn('[shortcuts] load failed', message);
        if (!silent || denied) {
          setError(denied
            ? 'Shortcuts for this tenant are no longer available'
            : 'Shortcuts could not be loaded. Try again.');
        }
      } finally {
        if (requestGen.current === gen && !silent) setLoading(false);
      }
    },
    [uid, tenantId, applyBundle, clearLocal]
  );

  useEffect(() => {
    const gen = ++requestGen.current;
    syncLauncherIconContext(`${uid || ''}|${launcherIconContextToken(tenantId)}`);
    clearLocal(tenantId || null);
    if (!uid || !tenantId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    load(gen).finally(() => {
      if (cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
      clearLocal(null);
    };
  }, [uid, tenantId, clearLocal, load]);

  useEffect(() => {
    if (!poll || !uid || !tenantId) return undefined;
    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      load(requestGen.current, { silent: true });
    };
    const timer = setInterval(tick, SHARED_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [poll, uid, tenantId, load]);

  const persistPersonal = useCallback(
    async next => {
      if (!uid || !tenantId) throw new Error('Sign in to save shortcuts');
      const coerced = coerceShortcutsPayload(next, tenantId, { strictIcons: true });
      const previous = bundleRef.current;
      applyBundle({ ...previous, personal: coerced });
      savingRef.current = true;
      setSaving(true);
      setSaveError(null);
      try {
        const saved = await saveUserShortcuts(tenantId, coerced);
        applyBundle({ ...bundleRef.current, personal: saved });
        setSaveError(null);
        return saved;
      } catch (err) {
        applyBundle(previous);
        const message = err?.message || 'Could not save shortcuts';
        setSaveError(message);
        toast.error(message);
        throw err;
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [uid, tenantId, applyBundle]
  );

  const persistShared = useCallback(
    async next => {
      if (!uid || !tenantId) throw new Error('Sign in to save shortcuts');
      if (!bundleRef.current.canManageShared) {
        throw new Error('Not allowed to manage tenant shortcuts');
      }
      const coerced = coerceSharedShortcutsPayload(next, tenantId, { strictIcons: true });
      const previous = bundleRef.current;
      const draft = { ...coerced, version: previous.shared?.version };
      applyBundle({ ...previous, shared: draft });
      sharedDirtyRef.current = true;
      savingRef.current = true;
      setSaving(true);
      setSaveError(null);
      try {
        const saved = await saveSharedShortcuts(tenantId, draft);
        applyBundle({ ...bundleRef.current, shared: saved, canManageShared: true });
        sharedDirtyRef.current = false;
        setSharedConflict(null);
        setSaveError(null);
        return saved;
      } catch (err) {
        const conflict = shortcutConflictFromError(err);
        if (conflict) {
          setSharedConflict({ code: conflict.code, message: conflict.message });
          setSaveError(conflict.message);
          toast.error(conflict.message);
          throw err;
        }
        applyBundle(previous);
        sharedDirtyRef.current = false;
        const message = err?.message || 'Could not save tenant shortcuts';
        setSaveError(message);
        toast.error(message);
        throw err;
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [uid, tenantId, applyBundle]
  );

  const activeTab = merged.tabs.find(tab => tab.id === activeTabId) || merged.tabs[0];
  const resolvedTabId = activeTab?.id || MY_LINKS_TAB_ID;
  const activeScope = activeTab?.scope || SCOPE_PERSONAL;
  const canEditActive = activeScope === SCOPE_PERSONAL || bundle.canManageShared;
  const activeShortcuts = useMemo(
    () => shortcutsForTab(merged, resolvedTabId),
    [merged, resolvedTabId]
  );

  const addTab = useCallback(
    async (title, scope = SCOPE_PERSONAL) => {
      const now = utcNow();
      if (scope === SCOPE_SHARED) {
        if (!bundle.canManageShared) {
          throw new Error('Not allowed to manage tenant shortcuts');
        }
        const tab = {
          id: newEntityId('stab'),
          title: String(title || 'Tenant links').trim() || 'Tenant links',
          order: bundle.shared.tabs.length,
          system: false,
          scope: SCOPE_SHARED,
          createdAt: now,
          updatedAt: now,
        };
        const saved = await persistShared({ ...bundle.shared, tabs: [...bundle.shared.tabs, tab] });
        setActiveTabId(tab.id);
        return saved;
      }
      const tab = {
        id: newEntityId('tab'),
        title: String(title || 'New tab').trim() || 'New tab',
        order: bundle.personal.tabs.length,
        system: false,
        scope: SCOPE_PERSONAL,
        createdAt: now,
        updatedAt: now,
      };
      const saved = await persistPersonal({
        ...bundle.personal,
        tabs: [...bundle.personal.tabs, tab],
      });
      setActiveTabId(tab.id);
      return saved;
    },
    [bundle.personal, bundle.shared, bundle.canManageShared, persistPersonal, persistShared]
  );

  const renameTab = useCallback(
    async (tabId, title) => {
      const nextTitle = String(title || '').trim();
      if (!nextTitle) return null;
      const tab = merged.tabs.find(item => item.id === tabId);
      if (!tab) return null;
      if (tab.scope === SCOPE_SHARED && !bundle.canManageShared) {
        throw new Error('Not allowed to manage tenant shortcuts');
      }
      if (tab.scope === SCOPE_SHARED) {
        return persistShared({
          ...bundle.shared,
          tabs: bundle.shared.tabs.map(item =>
            item.id === tabId ? { ...item, title: nextTitle, updatedAt: utcNow() } : item
          ),
        });
      }
      return persistPersonal({
        ...bundle.personal,
        tabs: bundle.personal.tabs.map(item =>
          item.id === tabId ? { ...item, title: nextTitle, updatedAt: utcNow() } : item
        ),
      });
    },
    [
      merged.tabs,
      bundle.personal,
      bundle.shared,
      bundle.canManageShared,
      persistPersonal,
      persistShared,
    ]
  );

  const deleteTab = useCallback(
    async tabId => {
      const tab = merged.tabs.find(item => item.id === tabId);
      if (!tab) return null;
      if (tab.system || tab.id === MY_LINKS_TAB_ID) {
        throw new Error('My Links cannot be deleted');
      }
      if (tab.scope === SCOPE_SHARED && !bundle.canManageShared) {
        throw new Error('Not allowed to manage tenant shortcuts');
      }
      if (tab.scope === SCOPE_SHARED) {
        const saved = await persistShared({
          ...bundle.shared,
          tabs: bundle.shared.tabs.filter(item => item.id !== tabId),
          shortcuts: bundle.shared.shortcuts.filter(item => item.tabId !== tabId),
        });
        if (activeTabId === tabId) setActiveTabId(MY_LINKS_TAB_ID);
        return saved;
      }
      const saved = await persistPersonal({
        ...bundle.personal,
        tabs: bundle.personal.tabs.filter(item => item.id !== tabId),
        shortcuts: bundle.personal.shortcuts.filter(item => item.tabId !== tabId),
      });
      if (activeTabId === tabId) setActiveTabId(MY_LINKS_TAB_ID);
      return saved;
    },
    [
      merged.tabs,
      bundle.personal,
      bundle.shared,
      bundle.canManageShared,
      persistPersonal,
      persistShared,
      activeTabId,
    ]
  );

  const reorderTabs = useCallback(
    async (activeId, overId) => {
      const active = merged.tabs.find(item => item.id === activeId);
      const over = merged.tabs.find(item => item.id === overId);
      if (!active || !over || active.scope !== over.scope) return null;
      if (active.scope === SCOPE_SHARED && !bundle.canManageShared) {
        throw new Error('Not allowed to manage tenant shortcuts');
      }
      if (active.scope === SCOPE_SHARED) {
        return persistShared({
          ...bundle.shared,
          tabs: moveTabOrder(bundle.shared.tabs, activeId, overId),
        });
      }
      return persistPersonal({
        ...bundle.personal,
        tabs: moveTabOrder(bundle.personal.tabs, activeId, overId),
      });
    },
    [
      merged.tabs,
      bundle.personal,
      bundle.shared,
      bundle.canManageShared,
      persistPersonal,
      persistShared,
    ]
  );

  const upsertShortcut = useCallback(
    async input => {
      const url = normalizeShortcutUrl(input.url);
      if (!url) throw new Error('Enter a valid http or https URL');
      const now = utcNow();
      const tab = merged.tabs.find(item => item.id === (input.tabId || activeTabId));
      if (!tab) {
        throw new Error('That tab is no longer available');
      }
      if (tab.scope === SCOPE_SHARED && !bundle.canManageShared) {
        throw new Error('Not allowed to manage tenant shortcuts');
      }
      const scope = tab.scope || SCOPE_PERSONAL;
      const tabId = tab.id;
      const source = scope === SCOPE_SHARED ? bundle.shared : bundle.personal;
      const persist = scope === SCOPE_SHARED ? persistShared : persistPersonal;
      const existing = source.shortcuts.find(item => item.id === input.id);
      let nextShortcuts;
      const iconType = input.iconType === 'image' ? 'image' : 'catalog';
      if (iconType === 'image' && !input.iconImageAssetId) {
        throw new Error('Choose an image before saving');
      }
      const iconFields = iconType === 'image'
        ? { iconType, iconKey: null, iconImageAssetId: input.iconImageAssetId }
        : { iconType: 'catalog', iconKey: input.iconKey || 'website', iconImageAssetId: null };
      if (existing) {
        nextShortcuts = source.shortcuts.map(item =>
          item.id === existing.id
            ? {
                ...item,
                label: input.label,
                url,
                ...iconFields,
                tabId,
                scope,
                updatedAt: now,
              }
            : item
        );
      } else {
        nextShortcuts = [
          ...source.shortcuts,
          {
            id: newEntityId(scope === SCOPE_SHARED ? 'ssc' : 'sc'),
            tabId,
            label: input.label,
            url,
            ...iconFields,
            order: shortcutsForTab(source, tabId).length,
            scope,
            createdAt: now,
            updatedAt: now,
          },
        ];
      }
      return persist({ ...source, shortcuts: nextShortcuts });
    },
    [
      merged.tabs,
      activeTabId,
      bundle.personal,
      bundle.shared,
      bundle.canManageShared,
      persistPersonal,
      persistShared,
    ]
  );

  const deleteShortcut = useCallback(
    async shortcutId => {
      const sharedHit = bundle.shared.shortcuts.find(item => item.id === shortcutId);
      if (sharedHit) {
        if (!bundle.canManageShared) {
          throw new Error('Not allowed to manage tenant shortcuts');
        }
        return persistShared({
          ...bundle.shared,
          shortcuts: bundle.shared.shortcuts.filter(item => item.id !== shortcutId),
        });
      }
      return persistPersonal({
        ...bundle.personal,
        shortcuts: bundle.personal.shortcuts.filter(item => item.id !== shortcutId),
      });
    },
    [bundle.personal, bundle.shared, bundle.canManageShared, persistPersonal, persistShared]
  );

  const reorderShortcuts = useCallback(
    async (activeId, overId) => {
      if (!canEditActive) return null;
      const source = activeScope === SCOPE_SHARED ? bundle.shared : bundle.personal;
      const persist = activeScope === SCOPE_SHARED ? persistShared : persistPersonal;
      const current = shortcutsForTab(source, activeTabId);
      const nextForTab = moveShortcutOrder(current, activeId, overId);
      return persist(replaceTabShortcuts(source, activeTabId, nextForTab));
    },
    [
      canEditActive,
      activeScope,
      bundle.personal,
      bundle.shared,
      persistPersonal,
      persistShared,
      activeTabId,
    ]
  );

  return {
    payload: { ...bundle.personal, tenantId: bundle.personal.tenantId || tenantId },
    personal: bundle.personal,
    shared: bundle.shared,
    canManageShared: bundle.canManageShared,
    tabs: merged.tabs,
    allShortcuts: merged.shortcuts,
    activeTabId,
    activeTab,
    activeScope,
    canEditActive,
    setActiveTabId,
    activeShortcuts,
    loading,
    error,
    saveError,
    saving,
    addTab,
    renameTab,
    deleteTab,
    reorderTabs,
    upsertShortcut,
    deleteShortcut,
    reorderShortcuts,
    saveSharedDocument: persistShared,
    sharedConflict,
    retryLoad: () => load(++requestGen.current),
    reloadShared: () => {
      sharedDirtyRef.current = false;
      setSharedConflict(null);
      return load(++requestGen.current);
    },
  };
}
